import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { fileDetailsStore } from "../FileDetailsStore";
import { Button } from "Components/General/Button";
import { I18N_NS } from "Config/i18n";
import { observer } from "mobx-react";
import { Modal } from "Components/General/Modal/GenericModal";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { EditableTable, EditableColumnProps } from "Components/General/EditableTable";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { MetadataEditInput } from "./MetadataEditInput";

interface ChangeMetadataProps extends WithTranslation {
  fileMetadata: API_FileMetadata[];
  /* disable button to show modal when file metadata is not loaded yet */
  disabled: boolean;
  reFetch: () => Promise<void>;
}

export interface MetadataWithValues extends API_Metadata {
  _currentValue?: string;
}

interface ChangeMetadataState {
  visible: boolean;
  metadataForEdit: MetadataWithValues[];
  metadataToSubmit: API_FileMetadata[];
  loading: boolean;
}

export const ChangeMetadata = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<ChangeMetadataProps, ChangeMetadataState> {
      constructor(props: ChangeMetadataProps) {
        super(props);
        this.state = {
          loading: false,
          visible: false,
          metadataForEdit: [],
          metadataToSubmit: [],
        };
      }

      fetchMetadataToEdit = async () => {
        try {
          const { fileMetadata } = this.props;
          const response = await API.getData<API_Metadata[]>(
            API_URL.METADATA.getDefinitionsFull(projectsDataStore.projectId)
          )
            // leave only manual metadata (type = 0)
            .then((res) => res.filter((item) => item.type === 0));

          const metadataForEdit: MetadataWithValues[] = response.reduce((arr: MetadataWithValues[], current) => {
            const metadataDefinition = fileMetadata.find((item) => item.definitionId === current.definitionId);
            if (metadataDefinition) {
              (current as MetadataWithValues)._currentValue = metadataDefinition.value;
              (current as MetadataWithValues).id = metadataDefinition.id;
            }
            arr.push(current);
            return arr;
          }, []);
          this.setState({ metadataForEdit });
        } catch (error) {
          throw error;
        }
      };

      saveMetadata = async () => {
        try {
          const { t } = this.props;
          this.setState({ loading: true });
          const formBody: API_FileMetadata[] = this.state.metadataToSubmit.slice();
          await API.editData(
            API_URL.FILES.updateFileMetadataForEdit(projectsDataStore.projectId, fileDetailsStore.data.id),
            formBody
          );
          this.setState({ visible: false });
          notificationSuccess(t("Success"), t("Metadata updated"));
          this.props.reFetch();
        } catch (error) {
          throw error;
        } finally {
          this.setState({ loading: false });
        }
      };

      showModal = () => {
        this.setState({ visible: true });
        this.fetchMetadataToEdit();
      };

      handleCancel = () => {
        this.setState({
          loading: false,
          visible: false,
          metadataForEdit: [],
          metadataToSubmit: [],
        });
      };

      handleEdit = async (value) => {
        const { metadataForEdit, metadataToSubmit } = this.state;
        const recordIdx = metadataForEdit.findIndex((item) => item.definitionId === value.definitionId);
        if (recordIdx === -1) {
          console.warn("Record not found. value = ", JSON.stringify(value));
          return;
        }
        const record = metadataForEdit[recordIdx];
        const newMetadataToSubmit: API_FileMetadata = {
          definitionId: record.definitionId,
          value: value._currentValue,
          key: record.key,
          id: record.id,
        };
        const existingRecordIdx = metadataToSubmit.findIndex(
          (item) => item.definitionId === newMetadataToSubmit.definitionId
        );
        if (existingRecordIdx === -1) {
          metadataToSubmit.push(newMetadataToSubmit);
        } else {
          metadataToSubmit[existingRecordIdx] = newMetadataToSubmit;
        }
        metadataForEdit[recordIdx]._currentValue = value._currentValue;
        // Improve this if you can.
        // Hack to update a cell after editing (display new value)
        this.setState({ metadataToSubmit, metadataForEdit: [] }, () => {
          this.setState({ metadataForEdit });
        });
      };

      render() {
        const { t, disabled } = this.props;
        const { visible, metadataForEdit, metadataToSubmit, loading } = this.state;
        const columns: EditableColumnProps<MetadataWithValues>[] = [
          {
            title: t("Name"),
            dataIndex: "key",
            editable: false,
          },
          {
            title: t("Description"),
            dataIndex: "description",
            editable: false,
          },
          {
            title: t("Value"),
            dataIndex: "_currentValue",
            editable: true,
            customInput: MetadataEditInput,
            inputType: "custom",
          },
        ];

        return (
          <div>
            {fileDetailsStore.data.fileActions?.editMetadata && (
              <div className="text-right">
                <Button type="primary" disabled={disabled} onClick={this.showModal}>
                  {t("Change metadata")}
                </Button>
              </div>
            )}
            <Modal
              onCancel={this.handleCancel}
              open={visible}
              title={t("Change metadata")}
              zIndex={1010}
              footer={null}
              style={{ maxWidth: "1200px" }}
              width="100%"
            >
              <EditableTable<MetadataWithValues>
                name="changeMetadata"
                rowIdKey="definitionId"
                key="id"
                columns={columns}
                dataSource={metadataForEdit}
                onEdit={this.handleEdit}
                showAddRow={false}
              />
              <div className="text-right mt-1">
                <Button
                  type="primary"
                  loading={loading}
                  disabled={!metadataToSubmit.length}
                  onClick={this.saveMetadata}
                >
                  {t("Save")}
                </Button>
              </div>
            </Modal>
          </div>
        );
      }
    }
  )
);
